import React, { useState } from "react";
import GenericChart from "../GenericChart";
import styled from "styled-components";
import { useThemeContext } from "contexts/ThemeProvider";
import { useChainId } from "lib/chains";
import {
  formatNumberStats,
  tooltipFormatterPercent,
  tooltipFormatterPercent1,
  tooltipLabelFormatterPercent,
  yaxisFormatterPercent,
} from "lib/helper";
import { formatNumber } from "lib/numbers";
import useOpenInterestMerged from "hooks/useOpenInterestMerged";
import MergeAnalyticsChart from "../MergeAnalyticsChart";
import { CHART_COLORS, convertOpenInterestIntoPieData } from "utils/analyticsV3";
import StatPieCharts from "pages/AnalyticsV3/components/Stats/StatPieCharts";

const OpenInterest = ({ loading, data }) => {
  const { isLightTheme } = useThemeContext();
  const { chainId } = useChainId();
  const openInterest = convertOpenInterestIntoPieData(data?.assets);

  const dataLongShort = [
    {
      title: "🟢 Long Open Interest",
      value: formatNumberStats(data?.openInterest?.long || 0, { currency: true }),
    },
    {
      title: "🔴 Short Open Interest",
      value: formatNumberStats(data?.openInterest?.short || 0, { currency: true }),
    },
    {
      title: "Long Dominance",
      value: `${
        data?.openInterest?.long
          ? formatNumber((data?.openInterest?.long / (data?.openInterest?.long + data?.openInterest?.short)) * 100)
          : 0
      }%`,
    },
  ];

  return (
    <OpenInterestContent>
      <div className="title">Open Interest (V1 and V2)</div>
      <div className="overview-interest">
        {dataLongShort.map((item, idx) => (
          <div className="item-overview" key={idx}>
            <span className="title-overview">{item.title}</span>
            {loading ? (
              <div className="skeleton-box" style={{ height: "25px", width: "100px" }} />
            ) : (
              <div className="value-overview">{item.value}</div>
            )}
          </div>
        ))}
      </div>
      <div className="interest-chart">
        <div className="chart-cell-primary rounded chart-interest-left">
          <MergeAnalyticsChart
            loading={loading}
            title="Open Interest"
            height={500}
            data={data?.openInterest?.chartData || []}
            // controls={{
            //   convertToPercents: convertToPercents,
            // }}
            yaxisDataKey="all"
            items={data?.openInterest?.chartKeys || []}
            type="Bar"
          />
        </div>
        <div className="chart-interest-right">
          <div className="chart-cell-primary rounded ">
            <MergeAnalyticsChart
              loading={loading}
              title="Long VS Short Open Interest"
              height={200}
              data={
                data?.openInterest?.totalOpenInterest?.map((item) => ({
                  all: item.all,
                  shortOpenInterest: item.totalShort,
                  longOpenInterest: item.totalLong,
                  timestamp: item.timestamp,
                })) || []
              }
              // controls={{
              //   convertToPercents: convertToPercents,
              // }}
              yaxisDataKey="all"
              items={[
                { key: "shortOpenInterest", name: "Short", color: CHART_COLORS[7] },
                { key: "longOpenInterest", name: "Long", color: CHART_COLORS[15] },
              ]}
              type="Bar"
            />
          </div>
          <div className="chart-cell-primary rounded">
            <MergeAnalyticsChart
              loading={loading}
              title="Long VS Short Weighting"
              height={200}
              // controls={{
              //   convertToPercents: convertToPercents,
              // }}
              yaxisTickFormatter={yaxisFormatterPercent}
              tooltipFormatter={tooltipFormatterPercent}
              tooltipLabelFormatter={tooltipLabelFormatterPercent}
              data={
                data?.openInterest?.totalOpenInterest?.map((item) => ({
                  shortWeight:
                    item.totalShort && item.totalLong ? (item.totalShort / item.all) * 100 : item?.totalShort ? 100 : 0,
                  longWeight:
                    item.totalShort && item.totalLong ? (item.totalLong / item.all) * 100 : item?.totalLong ? 100 : 0,
                  timestamp: item.timestamp,
                  all: 100,
                })) || []
              }
              // controls={{
              //   convertToPercents: convertToPercents,
              // }}
              yaxisDataKey="all"
              items={[
                { key: "shortWeight", name: "Short", color: CHART_COLORS[7] },
                { key: "longWeight", name: "Long", color: CHART_COLORS[15] },
              ]}
              type="Bar"
            />
          </div>
        </div>
      </div>
      <div className="interest-pie">
        <StatPieCharts
          data={openInterest?.data}
          originData={openInterest?.originData}
          chainId={chainId}
          isLightTheme={isLightTheme}
        />
      </div>
    </OpenInterestContent>
  );
};

export default OpenInterest;
const OpenInterestContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  width: 100%;

  .overview-interest {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;
    width: 100%;
  }
  .item-overview {
    display: flex;
    padding: 24px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 16px;
    background: #14161f;
  }
  .title-overview {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%;
    color: var(--Text-Text_Secondary, rgba(255, 255, 255, 0.6));
  }
  .value-overview {
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 140%;
  }
  .interest-chart {
    width: 100%;
    display: flex;
    gap: 24px;
    @media screen and (max-width: 900px) {
      flex-direction: column;
    }
  }
  .chart-interest-right {
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
  }
  .chart-interest-left {
    flex: 1;
  }
  .interest-pie {
    width: 100%;
  }
`;
